import { useEffect, useId, useMemo } from "react";
import ProductCategoryDistributionFormItem from "./ProductCategoryDistributionFormItem";
import { formatCurrency, formatDecimalUnit } from "@/common/utils/formatters";
import Select from "@/common/components/Select";
import { useFieldArray, useFormContext } from "react-hook-form";
import { unFormatCurrency, unFormatUnknownUnit } from "@/common/utils/unFormatters";
import { CreateGovernmentAllocationDistributionQuantityValidationSchema, CreateGovernmentAllocationDistributionValidationSchema } from '../../schemas/createGovernmentAllocationDistribution.schema';
import { CompanyGovernmentAllocationDistribution, CompanyGovernmentAllocationProductCategoriesDistribution } from '../../types/governmentAllocationDistribution.type';
import { Option } from '@/common/types/option';
import { GovernmentAllocationAllowedCategoriesAndProducts, GovernmentAllocationAllowedCategoryAndProductData } from '../../types/governmentAllocation.type';
import { useGovernmentAllocationsAllowedCategoriesAndProducts } from '../../hooks/useGovernmentAllocationsAllowedCategoriesAndProducts';
import { useParams } from 'react-router-dom';
import { GovernmentAllocationsAllowedCategoriesAndProductsTransformer } from '../../services/governmentAllocationAllowedCategoriesAndProductsTransformer';
import { ErrorMessage } from "@/common/components/ErrorMessage";

interface GovernmentAllocationDistributionProductCategoriesFormProps {
  distribution: CompanyGovernmentAllocationDistribution;
  companyBranchId?: string;
  isConsumptionControlledByQuantity?: boolean;
}

const GovernmentAllocationDistributionProductCategoriesForm = ({
  distribution,
  companyBranchId,
  isConsumptionControlledByQuantity,
}: GovernmentAllocationDistributionProductCategoriesFormProps) => {
  const uniqueId = useId();

  const { id } = useParams();

  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<
    | CreateGovernmentAllocationDistributionValidationSchema
    | CreateGovernmentAllocationDistributionQuantityValidationSchema
  >();

  const {
    data: allowedCategoriesAndProducts,
    isFetching: isAllowedCategoriesAndProductsFetching,
    isPending: isAllowedCategoriesAndProductsPending
  } = useGovernmentAllocationsAllowedCategoriesAndProducts<GovernmentAllocationAllowedCategoriesAndProducts>({
    governmentAllocationId: id,
    filters: { companyBranchId },
  });

  const allowedCategoriesOptions = useMemo(() => {
    return isAllowedCategoriesAndProductsPending
      ? []
      : GovernmentAllocationsAllowedCategoriesAndProductsTransformer.optionsTransformer('categories', isConsumptionControlledByQuantity)(allowedCategoriesAndProducts || {})
  }, [isAllowedCategoriesAndProductsPending]);

  const {
    fields: productCategoriesDistributions,
    append: appendProductCategoryDistribution,
    remove: removeProductCategoryDistribution,
  } = useFieldArray({
    control,
    name: 'product_categories_distributions',
  });

  const setTotalValue = () => {
    if (isConsumptionControlledByQuantity) {
      const productCategoriesTotalQuantitySum = productCategoriesDistributions.reduce((sum, _, index) => {
        const productCategoryTotalQuantity = watch(`product_categories_distributions.${index}.total_quantity` as `product_categories_distributions.${number}.total_value`);

        return sum + (productCategoryTotalQuantity ? unFormatUnknownUnit(productCategoryTotalQuantity) : 0);
      }, 0);

      setValue('total_quantity' as 'total_value', formatDecimalUnit(productCategoriesTotalQuantitySum));
    } else {
      const productCategoriesTotalValueSum = productCategoriesDistributions.reduce((sum, _, index) => {
        const productCategoryTotalValue = watch(`product_categories_distributions.${index}.total_value`);

        return sum + (productCategoryTotalValue ? unFormatCurrency(productCategoryTotalValue) : 0);
      }, 0);

      setValue('total_value', formatCurrency(productCategoriesTotalValueSum));
    }
  };

  useEffect(() => {
    productCategoriesDistributions?.length > 0 && setTotalValue();
  }, [productCategoriesDistributions?.length]);

  return (
    <div className="flex flex-col gap-4">
      <Select
        placeholder="Selecione um serviço"
        isLoading={isAllowedCategoriesAndProductsFetching}
        options={allowedCategoriesOptions?.filter((productCategoryOption) => {
          return !productCategoriesDistributions?.some(({ product_category }) => product_category?.id === productCategoryOption.value);
        }) || []}
        value=""
        onChange={(value) => {
          const newValue = { ...(value as GovernmentAllocationAllowedCategoryAndProductData & Option) }

          if (isConsumptionControlledByQuantity) {
            appendProductCategoryDistribution({
              product_category: {
                id: newValue.value,
                display_name: newValue.display_name,
                products_count: ''
              },
              products_distributions: [],
              total_quantity: newValue?.consumed_quantity
                ? formatDecimalUnit(newValue?.consumed_quantity || '')
                : formatDecimalUnit(0),
            });
          } else {
            appendProductCategoryDistribution({
              product_category: {
                id: newValue.value,
                display_name: newValue.display_name,
                products_count: ''
              },
              products_distributions: [],
              total_value: newValue?.consumed_total
                ? formatCurrency(Number(newValue?.consumed_total || ''))
                : formatCurrency(0),
            });
          }

          setTotalValue();
        }}
      />

      {!!errors.product_categories_distributions?.message && (
        <ErrorMessage
          name="product_categories_distributions"
          message={errors.product_categories_distributions?.message}
        />
      )}

      {productCategoriesDistributions.map((productCategoryDistribution, index) => (
        <ProductCategoryDistributionFormItem
          key={`${uniqueId}-${productCategoryDistribution?.product_category?.id}`}
          productCategoryIndex={index}
          productCategoryDistribution={productCategoryDistribution as CompanyGovernmentAllocationProductCategoriesDistribution}
          onDelete={() => {
            removeProductCategoryDistribution(index);
            setTotalValue();
          }}
          onTotalValueChange={setTotalValue}
          distribution={distribution}
          companyBranchId={companyBranchId}
          isConsumptionControlledByQuantity={isConsumptionControlledByQuantity}
        />
      ))}
    </div>
  );
};

export default GovernmentAllocationDistributionProductCategoriesForm;
